/**
 * useTools Hook
 *
 * Hook for fetching and managing available tools data
 * Uses React Query for caching and automatic refetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toolsApi } from '../../../lib/api/tools';
import { useI18n } from '../../../i18n/I18nContext';
import type { ToolValidationRequest } from '../../../types/tools';

/**
 * Query keys for tools data
 */
export const toolsQueryKeys = {
  availableTools: (language: 'cn' | 'en') =>
    ['tools', 'available', { language }] as const,
  availableMCP: (forceReconnect: boolean, language: 'cn' | 'en') =>
    ['tools', 'mcp', 'available', { forceReconnect, language }] as const,
  mcpStatus: () => ['mcp', 'status'] as const,
};

/**
 * Hook to fetch available custom and builtin tools
 * Automatically uses the current language from i18n context
 */
export function useAvailableTools() {
  const { language: i18nLanguage } = useI18n();

  // Map i18n language to API language ('zh' -> 'cn', 'en' -> 'en')
  const apiLanguage: 'cn' | 'en' = i18nLanguage === 'zh' ? 'cn' : 'en';

  return useQuery({
    queryKey: toolsQueryKeys.availableTools(apiLanguage),
    queryFn: () => toolsApi.fetchAvailableTools(apiLanguage),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch available MCP servers and tools
 * Automatically uses the current language from i18n context
 */
export function useAvailableMCPServers(forceReconnect = false) {
  const queryClient = useQueryClient();
  const { language: i18nLanguage } = useI18n();

  // Map i18n language to API language ('zh' -> 'cn', 'en' -> 'en')
  const apiLanguage: 'cn' | 'en' = i18nLanguage === 'zh' ? 'cn' : 'en';

  // Use a stable query key (without forceReconnect) so data is shared
  const queryKey = toolsQueryKeys.availableMCP(false, apiLanguage);

  const query = useQuery({
    queryKey,
    queryFn: () => toolsApi.fetchAvailableMCPServers(forceReconnect, apiLanguage),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Function to trigger refresh with force reconnect
  // Directly fetch with force_reconnect=true and update the cache
  const refresh = async () => {
    try {
      const data = await toolsApi.fetchAvailableMCPServers(true, apiLanguage);
      // Update the cache with the fresh data
      queryClient.setQueryData(queryKey, data);
    } catch (error) {
      console.error('Failed to refresh MCP servers:', error);
      // Invalidate to trigger a refetch on error
      queryClient.invalidateQueries({ queryKey });
    }
  };

  return {
    ...query,
    refresh,
  };
}

/**
 * Hook to validate tools configuration
 */
export function useValidateTools() {
  return useMutation({
    mutationFn: (config: ToolValidationRequest) =>
      toolsApi.validateToolsConfig(config),
  });
}

/**
 * Hook to fetch MCP connection pool status
 * Auto-refreshes every 10 seconds when enabled
 */
export function useMCPStatus(autoRefresh = true) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: toolsQueryKeys.mcpStatus(),
    queryFn: () => toolsApi.fetchMCPStatus(),
    staleTime: 5 * 1000, // 5 seconds
    refetchInterval: autoRefresh ? 10 * 1000 : false, // Auto-refresh every 10s
    retry: 1,
  });

  // Manual refresh function
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: toolsQueryKeys.mcpStatus() });
  };

  // Refresh connections (POST /mcp/refresh)
  const refreshConnections = async () => {
    try {
      await toolsApi.refreshMCPConnections();
      // Invalidate the status query to fetch fresh data
      await queryClient.invalidateQueries({ queryKey: toolsQueryKeys.mcpStatus() });
    } catch (error) {
      console.error('Failed to refresh MCP connections:', error);
      throw error;
    }
  };

  return {
    ...query,
    refresh,
    refreshConnections,
  };
}
